

import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Col,
  Row,
  Label,
} from "reactstrap";
import TextEditor from "components/editor/TextEditor";
import { useFormik } from "formik";
import * as Yup from "yup";
import { httpRequest } from "utils/httpRequest";
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"

const AddPodcast = () => {
  const [videoLink, setVideoLink] = useState("")
  const [buttonLoading, setButtonLoading] = useState(false);
  const [imageLink, setImageLink] = useState(null);

  const navigate = useNavigate()

  // Validation Schema
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Podcast title is required")
      .min(3, "Title must be at least 3 characters"),
    description: Yup.string()
      .required("Description is required"),
    video: Yup.mixed()
      .required("A podcast file is required")
      .test(
        "fileType",
        "Only video files are allowed",
        (value) => value && ["video/mp4"].includes(value.type)
      ),
    image: Yup.mixed()
      .required("Cover image is required")
      .test(
        "fileSize",
        "Image must be smaller than 5MB",
        (value) => value && value.size <= 5000000
      )
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      video: null,
      image: null,
      imagePreview: null
    },
    validationSchema,
    onSubmit: async (values) => {
      formik.setTouched({
        title: true,
        description: true,
        video: true,
        image: true
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) return false;
      setButtonLoading(true);
      try {
        const response = await httpRequest(
          "podcast/add",
          "POST",
          {
            title: values.title,
            description: values.description,
            video: videoLink,
            image: imageLink
          },
          {},
          true,
          true
        );
        if (response?.code === 200) {

          toast.success("Podcast added successfully")
          formik.resetForm({
            values: {
              title: "",
              description: "",
              video: null,
              image: null,
              imagePreview: null
            }
          })
          setImageLink(null);
          navigate("/admin/podcast-management")
        }
      } catch (error) {
        console.log(error);

      } finally {
        setButtonLoading(false);
      }
    },
  });

  // Memoize the video URL to prevent recreation on every render
  const videoPreviewUrl = useMemo(() => {
    if (formik.values.video) {
      return URL.createObjectURL(formik.values.video);
    }
    return null;
  }, [formik.values.video]);

  // Clean up the object URL when component unmounts or video changes
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);

  const getUploadedVideoLink = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await httpRequest(
        "admin/UploadMedia",
        "POST",
        formData,
        {},
        true,
        true
      );
      return response?.filePath;
    } catch (error) {
      console.log("Upload error:", error);
      throw error;
    }
  };

  const handleVideoChange = async (event) => {
    const file = event.currentTarget.files[0]

    const allowedTypes = ["video/mp4"];

    if (!allowedTypes.includes(file?.type)) {
      toast.error("Unsupported video format. Only MP4 allowed.");
      formik.setFieldValue("video", null);
      return;
    }



    formik.setFieldValue("video", file);
    try {
      const uploadedVideoLink = await getUploadedVideoLink(file);
      setVideoLink(uploadedVideoLink);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  }



  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    formik.setFieldError("image", undefined);

    // First update Formik state and show preview
    await formik.setFieldValue("image", file, false);
    const imageUrl = URL.createObjectURL(file);
    formik.setFieldValue("imagePreview", imageUrl, false);

    // Then upload the image after Formik state is updated
    try {
      const uploadedImageLink = await getUploadedVideoLink(file); // Use the file directly
      setImageLink(uploadedImageLink);

      if (formik.submitCount > 0) { // Only validate if form was submitted before
        await formik.validateField("image");
      }
    } catch (error) {
      console.log("Upload failed:", error);
    }
  };







  return (
    <Row className="mt-4 justify-content-center">
      <Col lg="8">
        <Card className="bg-secondary shadow border-0">
          <CardHeader className="bg-transparent pb-3">
            <div className="text-muted text-center mt-2 mb-3">
              <small>Add New Podcast</small>
            </div>
          </CardHeader>
          <CardBody className="px-lg-5 py-lg-4">
            <Form onSubmit={formik.handleSubmit}>
              {/* Podcast Title */}
              <FormGroup className="mb-3">
                <Label className="form-control-label">Podcast Title</Label>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-book-bookmark" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Enter podcast title"
                    type="text"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger small mt-1">
                    {formik.errors.title}
                  </div>
                )}
              </FormGroup>

              {/* Description */}
              <FormGroup>
                <Label className="form-control-label">Podcast Description</Label>
                <TextEditor
                  value={formik.values.description}
                  onChange={(html) => formik.setFieldValue("description", html)}
                  onBlur={() => formik.setFieldTouched("description", true)}
                  placeholder="Write the podcast description..."
                  hideImageAndLink={false}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className="text-danger small mt-1">
                    {formik.errors.description}
                  </div>
                )}
              </FormGroup>

              {/* Video File */}
              {/* <FormGroup className="mb-4">
                <Label className="form-control-label">Podcast Video</Label> */}

                {/* Upload button and status */}
                {/* <div className="d-flex align-items-center gap-3 mb-3">
                  {formik.values.video ? (
                    <span className="text-success">
                      <i className="ni ni-check-bold" />
                      {formik.values.video.name || "Video selected"}
                    </span>
                  ) : (
                    ""
                  )}

                  <label className="btn btn-sm btn-primary mb-0 ml-2">
                    <i className="ni ni-cloud-upload-96 mr-1" />
                    {formik.values.video ? "Change Video" : "Upload Video"}
                    <input
                      type="file"
                      accept="video/mp4"
                      onChange={handleVideoChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div> */}

                {/* Video preview */}
                {/* {videoPreviewUrl && (
                  <div className="mt-2">
                    <video
                      controls

                      style={{
                        width: "300px",
                        height: "180px",
                        borderRadius: "6px",
                        objectFit: "cover",
                        display: "block"
                      }}
                      src={videoPreviewUrl}
                    />
                  </div>
                )}

                {formik.touched.video && formik.errors.video && (
                  <div className="text-danger small mt-1">
                    {formik.errors.video}
                  </div>
                )}
              </FormGroup>
              <FormGroup className="mb-4">
                <Label className="form-control-label" htmlFor="imageUpload">
                  Upload Cover Image
                </Label>
                <input
                  type="file"
                  name="image"
                  id="imageUpload"
                  accept="image/*"
                  onChange={handleImageChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.image && formik.errors.image && (
                  <div className="text-danger small mt-1">
                    {formik.errors.image}
                  </div>
                )}
              </FormGroup> */}

              {/* Image Preview */}
              {/* {formik.values.imagePreview && (
                <div className="mt-3">
                  <img
                    src={formik.values.imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "25%",
                      height: "auto",
                      borderRadius: "0.25rem"
                    }}
                  />
                </div>
              )} */}

              <div class="container text-center addflexwrap-video">
                <div class="row">
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-4 addflex-videoimage">
                      <Label className="form-control-label"> Video</Label>

                      {/* Upload button and status */}
                      <div className="d-flex align-items-center gap-3 mb-3 addmrginresponsive-video">
                        {formik.values.video ? (
                          <span className="text-success">
                            <i className="ni ni-check-bold" />
                            {formik.values.video.name || "Video selected"}
                          </span>
                        ) : (
                          ""
                        )}

                        <label className="btn btn-sm btn-primary mb-0 ml-2 addp-addvideo">
                          <i className="ni ni-cloud-upload-96 mr-1" />
                          {formik.values.video ? "Change Video" : "Upload Video"}
                          <input
                            type="file"
                            accept="video/mp4"
                            onChange={handleVideoChange}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>

                      {/* Video preview */}
                      {videoPreviewUrl && (
                        <div className="mt-2 w-100" >
                          <video
                            controls


                            style={{
                              width: "100%",
                              height: "180px",
                              borderRadius: "6px",
                              objectFit: "cover",
                              display: "block"
                            }}
                            src={videoPreviewUrl}
                          />
                        </div>
                      )}

                      {formik.touched.video && formik.errors.video && (
                        <div className="text-danger small mt-1">
                          {formik.errors.video}
                        </div>
                      )}
                    </FormGroup>
                  </div>
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-4 addflex-videoimage reducemargin-image">
                      <Label className="form-control-label" htmlFor="imageUpload">
                        Upload Cover Image
                      </Label>
                      <input
                        className="btn btn-sm btn-primary addw-imagebtn"
                        type="file"
                        name="image"
                        id="imageUpload"
                        accept="image/*"
                        onChange={handleImageChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.image && formik.errors.image && (
                        <div className="text-danger small mt-1">
                          {formik.errors.image}
                        </div>
                      )}
                    </FormGroup>

                    {/* Image Preview */}
                    {formik.values.imagePreview && (
                      <div className="mt-3 addmarginbtm-responsiveimage">
                        <img
                          src={formik.values.imagePreview}
                          alt="Preview"
                          className="addwh-image"
                          style={{
                            maxWidth: "25%",
                            height: "auto",
                            borderRadius: "0.25rem"
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center d-flex align-items-center justify-content-center">
                <Button className="mb-3 d-flex align-items-center justify-content-center" color="primary" type="submit" disabled={buttonLoading}>
                  {buttonLoading ? <span className="button-loader"></span> : "Save Podcast"}
                </Button>

              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default AddPodcast;